import type { ComponentProps, ReactNode } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"

export type UsageReportBarRow = {
  key: string
  /** The human name ("Land register extract"). */
  label: ReactNode
  /** The code or a detail under the name, small and muted. */
  sublabel?: ReactNode
  /** The magnitude the bar is drawn from. */
  value: number
  /** Trailing columns after the number ("27 customers", "61 %"). */
  extra?: ReactNode
  /** Native tooltip for the row. */
  title?: string
}

export type UsageReportBarListProps = ComponentProps<"div"> & {
  rows: UsageReportBarRow[]
  /** Rows shown before "+ N more". */
  limit?: number
  /**
   * "max": each bar is its share of the largest row (a ranking). "total":
   * each bar is its share of all rows and the number shown is that share
   * in percent (a mix).
   */
  scale?: "max" | "total"
  /** Bar colour: green for activity, amber for things to act on. */
  tone?: "activity" | "warning"
  /** Width class of the label column. */
  labelWidth?: string
  emptyText: string
}

/**
 * A ranked list drawn as horizontal bars: the report's "most used tools",
 * "AI clients", "top customers". One component for every ranking on every
 * usage surface, so they align and read the same way.
 */
function UsageReportBarList(props: UsageReportBarListProps) {
  const {
    rows,
    limit = rows.length,
    scale = "max",
    tone = "activity",
    labelWidth = "w-48",
    emptyText,
    className,
    ...divProps
  } = props

  const visible = rows.slice(0, limit)
  const max = Math.max(1, ...visible.map((row) => row.value))
  const total = rows.reduce((sum, row) => sum + row.value, 0)
  const hiddenCount = rows.length - visible.length

  if (visible.length === 0) {
    return (
      <p className={cn("text-xs text-muted-foreground", className)}>
        {emptyText}
      </p>
    )
  }

  return (
    <div className={cn("flex flex-col gap-1.5", className)} {...divProps}>
      <ul className="flex flex-col gap-1.5">
        {visible.map((row) => {
          const share = total > 0 ? row.value / total : 0
          const width = scale === "total" ? share : row.value / max
          return (
            <li
              key={row.key}
              className="flex items-center gap-3 text-xs"
              title={row.title}
            >
              <span className={cn("flex shrink-0 flex-col", labelWidth)}>
                <span className="truncate font-medium">{row.label}</span>
                {row.sublabel != null && (
                  <span className="truncate font-mono text-[10px] text-muted-foreground">
                    {row.sublabel}
                  </span>
                )}
              </span>
              <span className="usage-report-bar">
                <span
                  className={cn(
                    "usage-report-bar-fill",
                    tone === "warning" && "usage-report-bar-fill--warning",
                  )}
                  style={{ width: `${Math.round(width * 1000) / 10}%` }}
                />
              </span>
              <span className="w-14 shrink-0 text-right tabular-nums">
                {scale === "total"
                  ? `${Math.round(share * 100)} %`
                  : formatNumber(row.value)}
              </span>
              {row.extra != null && (
                <span className="min-w-28 shrink-0 whitespace-nowrap text-right text-muted-foreground">
                  {row.extra}
                </span>
              )}
            </li>
          )
        })}
      </ul>
      {hiddenCount > 0 && (
        <p className="text-[11px] text-muted-foreground">
          + {hiddenCount} more
        </p>
      )}
    </div>
  )
}

export default UsageReportBarList
